import axiosInstance from '../../../services/axiosInstance';
import { API_V2 } from '../../../models/GWDomain';
import {
  CurrentConversation,
  InboxSummary,
  InboxTicket,
  InboxTicketDetail,
  KbArticle,
  KbResource,
  SendMessageResponse,
  UnreadCount,
} from '../models/types';
import type { AdriDownload, PendingAction, UiCommand } from '../../adri/services/adriApi';

const BASE = `${API_V2}/support`;

// ============================================================
// Chat del usuario
// ============================================================

export interface SendMessagePayload {
  content: string;
  route?: string;
  appVersion?: string;
  httpErrors?: Array<{ method: string; url: string; status: number; at: string }>;
  attachmentKey?: string;
  /** 051 — contexto del copiloto (opcionales; el envío es JSON). */
  screenId?: string;
  localId?: number;
  availableTargets?: string[];
  /** 051 T042 — borrador del importador recién subido, para que Adri lo narre. */
  draftId?: number;
}

/** 051 — campos aditivos de la respuesta cuando el copiloto está activo. */
export interface AdriSendExtras {
  requestId?: string;
  commands?: UiCommand[];
  pendingAction?: PendingAction | null;
  download?: AdriDownload | null;
}

/** Timeout largo: la respuesta del asistente puede tardar más que el default de 15s. */
export const sendSupportMessage = async (
  payload: SendMessagePayload,
): Promise<SendMessageResponse & AdriSendExtras> => {
  const { data } = await axiosInstance.post(`${BASE}/chat/messages`, payload, {
    timeout: 45000,
  });
  return data.data;
};

/** Sube una captura (JPG/PNG/WEBP ≤4MB) y devuelve la key para el próximo mensaje. */
export const uploadChatAttachment = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await axiosInstance.post(`${BASE}/chat/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });
  return data.data.key;
};

export const getCurrentConversation = async (): Promise<CurrentConversation | null> => {
  const { data } = await axiosInstance.get(`${BASE}/chat/current`);
  return data.data;
};

export const getUnreadCount = async (): Promise<UnreadCount> => {
  const { data } = await axiosInstance.get(`${BASE}/chat/unread-count`);
  return data.data;
};

export const escalateToHuman = async (): Promise<SendMessageResponse> => {
  const { data } = await axiosInstance.post(`${BASE}/chat/escalate`, {});
  return data.data;
};

export const submitFeedback = async (
  ticketId: number,
  thumbsUp: boolean,
  comment?: string,
): Promise<void> => {
  await axiosInstance.post(`${BASE}/chat/${ticketId}/feedback`, { thumbsUp, comment });
};

// ============================================================
// HQ (tenant de Prikly)
// ============================================================

export const getHqAccess = async (): Promise<boolean> => {
  try {
    const { data } = await axiosInstance.get(`${BASE}/hq/access`);
    return !!data.data?.isHq;
  } catch {
    return false;
  }
};

export interface InboxQuery {
  status?: number;
  category?: number;
  feedback?: 'up' | 'down' | 'none';
  search?: string;
  from?: string;
  to?: string;
  page?: number;
}

export const getHqTickets = async (
  query: InboxQuery,
): Promise<{
  items: InboxTicket[];
  total: number;
  page: number;
  pageSize: number;
  summary: InboxSummary;
}> => {
  const { data } = await axiosInstance.get(`${BASE}/hq/tickets`, { params: query });
  return data.data;
};

export const getHqTicketDetail = async (id: number): Promise<InboxTicketDetail> => {
  const { data } = await axiosInstance.get(`${BASE}/hq/tickets/${id}`);
  return data.data;
};

export const replyHqTicket = async (id: number, content: string): Promise<void> => {
  await axiosInstance.post(`${BASE}/hq/tickets/${id}/reply`, { content });
};

export const resolveHqTicket = async (id: number, content?: string): Promise<void> => {
  await axiosInstance.post(`${BASE}/hq/tickets/${id}/resolve`, content ? { content } : {});
};

// ============================================================
// Base de conocimientos (HQ)
// ============================================================

export const getKbArticles = async (filters: {
  status?: number;
  module?: string;
  search?: string;
}): Promise<KbArticle[]> => {
  const { data } = await axiosInstance.get(`${BASE}/hq/articles`, { params: filters });
  return data.data;
};

export const getKbArticle = async (id: number): Promise<KbArticle> => {
  const { data } = await axiosInstance.get(`${BASE}/hq/articles/${id}`);
  return data.data;
};

export const createKbArticle = async (article: Partial<KbArticle>): Promise<KbArticle> => {
  const { data } = await axiosInstance.post(`${BASE}/hq/articles`, article);
  return data.data;
};

export const updateKbArticle = async (
  id: number,
  article: Partial<KbArticle>,
): Promise<KbArticle> => {
  const { data } = await axiosInstance.put(`${BASE}/hq/articles/${id}`, article);
  return data.data;
};

export const uploadKbMedia = async (
  articleId: number,
  file: File,
  title: string,
): Promise<KbResource> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', title);
  const { data } = await axiosInstance.post(`${BASE}/hq/articles/${articleId}/media`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return data.data;
};

export const addKbVideo = async (
  articleId: number,
  title: string,
  url: string,
): Promise<KbResource> => {
  const { data } = await axiosInstance.post(`${BASE}/hq/articles/${articleId}/video`, {
    title,
    url,
  });
  return data.data;
};

export const deleteKbResource = async (articleId: number, resourceId: number): Promise<void> => {
  await axiosInstance.delete(`${BASE}/hq/articles/${articleId}/resources/${resourceId}`);
};

// ============================================================
// Ack instantáneo (sin IA): frase corta mientras llega la respuesta real
// ============================================================

export interface AckResponse {
  ack: string | null;
  kind: 'none' | 'escalation' | 'empathy' | 'topic' | 'generic';
}

export const getInstantAck = async (message: string, counter: number): Promise<AckResponse> => {
  const { data } = await axiosInstance.post(
    `${BASE}/chat/ack`,
    { message, counter },
    { timeout: 3000 },
  );
  return data.data;
};
